import { z } from 'zod';
import { ApiCallCredentialClassSchema } from '../admin/api-call.schemas';
import type { Permission } from '../permissions';

/**
 * Bus events emitted by the permission gate. Each authorisation decision
 * (granted, denied, unauthenticated) becomes one row in `_kernel.audit_event`
 * after IMM-56's subscriber lands. Payload identifies the attempted action
 * and, for denials, a stable machine-readable reason.
 */

const PermissionSchema = z.string().min(1) as z.ZodType<Permission>;

const AccessTargetSchema = z.object({
  permission: PermissionSchema,
  route: z.string().min(1),
  method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
});

/** The record vocabulary of an access entry's `read` block, shared by every read surface. */
export const AuditRecordTypeSchema = z.enum([
  'lead',
  'deal',
  'prospect',
  'contact',
  'company',
  'lead-partner',
  'property',
  'property-document',
  'property-media',
  'portal-listing',
  'media-file',
  'valuation-request',
  'valuation-document',
  'activity',
  'activity-record',
  'appointment',
  'email-thread',
  'email-message',
  'member',
  'invitation',
  'organization',
  'api-key',
  'connected-client',
  'conversation',
  'skill-instance',
  'skill-run',
  'skill-approval',
  'inquiry',
]);

export const AuditFileTypeSchema = z.enum([
  'property-document',
  'property-media',
  'expose',
  'valuation-report',
  'email-attachment',
  'media-file',
  'member-avatar',
]);

export const AccessReadSchema = z.object({
  kind: z.enum(['single', 'list', 'download']),
  // Grouped by type so a search returning several types needs no second shape.
  records: z.partialRecord(AuditRecordTypeSchema, z.array(z.string().min(1))),
  // The number of ids across every type, i.e. the number of records the call returned.
  count: z.number().int().nonnegative(),
  // Present iff kind === 'download'.
  file: z.object({ type: AuditFileTypeSchema, id: z.string().min(1) }).optional(),
});

export type AuditRecordType = z.infer<typeof AuditRecordTypeSchema>;
export type AuditFileType = z.infer<typeof AuditFileTypeSchema>;
export type AccessRead = z.infer<typeof AccessReadSchema>;

export const AccessGrantedPayloadSchema = AccessTargetSchema.extend({
  // null only for the valuation agent: no person stands behind the call.
  userId: z.string().min(1).nullable(),
  // `null` for admin-tier (cross-workspace by design); a UUID for workspace-tier callers.
  workspaceId: z.string().uuid().nullable(),
  // The key's id, for audit traceability. Set for the `api-key` and `workspace-mcp` credential classes.
  apiKeyId: z.string().uuid().optional(),
  // Optional only so rows written before IMM-1106.1 still parse; every emitter sets it.
  credentialClass: ApiCallCredentialClassSchema.optional(),
  // Set iff credentialClass is 'workspace-mcp-oauth'.
  connectedClientId: z.string().min(1).optional(),
  // Set iff credentialClass is 'valuation-agent'.
  valuationAgentTokenId: z.uuid().optional(),
  // Set iff credentialClass is 'impersonation'. The operator is the audit actor; the member is named here.
  impersonation: z
    .object({
      operatorId: z.string().min(1),
      impersonatedMemberId: z.string().min(1),
      sessionId: z.string().min(1),
    })
    .optional(),
  // Set iff the entry is a customer MCP tool call. `route` is then '/v1/mcp'.
  mcpToolId: z.string().min(1).optional(),
  // The records a successful read returned. Absent on a failed call and on a route that declares no read.
  read: AccessReadSchema.optional(),
  // Set when the read succeeded but its records could not be named; never `count: 0` (BL-093).
  readUnrecorded: z.literal(true).optional(),
  // Personal data (ADR 0015 §PII, amended by IMM-1106.6): full address and user agent, 90-day audit retention.
  // Present only on a customer read, and only while AUDIT_RECORD_CALLER_NETWORK is on.
  client: z.object({ ip: z.string().min(1), userAgent: z.string().nullable() }).optional(),
});

/**
 * Denial reasons:
 *  - `not-admin`           — caller is authenticated but not in the admin roster.
 *  - `permission-not-held` — caller is an admin but the permission isn't in their set.
 *  - `role-read-failed`    — error reading the actor's roles; fail-closed.
 */
export const AccessDeniedPayloadSchema = AccessTargetSchema.extend({
  // null only for the valuation agent: no person stands behind the call.
  userId: z.string().min(1).nullable(),
  workspaceId: z.string().uuid().nullable(),
  reason: z.enum(['not-admin', 'permission-not-held', 'role-read-failed']),
  // Present only when the caller authenticated with an API key — the key's id, for audit traceability.
  apiKeyId: z.string().uuid().optional(),
  credentialClass: z.string().optional(),
});

export const AccessUnauthenticatedPayloadSchema = z.object({
  permission: PermissionSchema,
  route: z.string().min(1),
  method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
});

// The platform-wide MCP surface switch (IMM-522.4). Audited always: disabling
// and re-enabling are containment actions attributable to an operator.
export const McpAccessSwitchChangedPayloadSchema = z.object({
  enabled: z.boolean(),
  actor: z.object({ type: z.literal('ADMIN'), id: z.string().min(1) }),
});

// The platform-wide switch over the CUSTOMER MCP surface (IMM-601.6). Audited
// always: containing a suspected leak — and lifting the containment — must be
// attributable to the operator who did it. The operator is carried as a bare
// `adminId` rather than an `actor` envelope because the audit mapper pins the
// actor type to ADMIN for this event: no other principal can flip it.
export const WorkspaceMcpSwitchChangedPayloadSchema = z.object({
  enabled: z.boolean(),
  adminId: z.string().min(1),
});

// One tool's exposure on the CUSTOMER MCP surface (IMM-625.2). Audited always
// and separately from the surface-wide switch above: withdrawing a single
// misbehaving tool and containing the whole surface are different decisions,
// and an incident review must be able to tell them apart. `adminId` is bare for
// the same reason as its sibling — no principal but an operator can write it.
export const McpToolExposureChangedPayloadSchema = z.object({
  toolId: z.string().min(1),
  exposed: z.boolean(),
  // The state enforced immediately before this write — `false` for both when
  // no row existed, per the parent PRD's "a tool with no exposure entry is not
  // exposed" rule. Lets an incident review tell a real transition from a
  // re-write of the value a tool already held (IMM-775.1).
  previousExposed: z.boolean(),
  previousReviewed: z.boolean(),
  adminId: z.string().min(1),
});

// A stale exposure entry cleared by an operator (IMM-625.3) — a row whose tool
// had already left the catalogue. Separate from `changed` above and not a
// withdrawal: nobody's effective set moves, because the tool it named was
// already unreachable. Conflating the two would make an incident review read a
// cleanup as a containment action.
export const McpToolExposureStaleEntryRemovedPayloadSchema = z.object({
  toolId: z.string().min(1),
  adminId: z.string().min(1),
});
